import { forwardRef, HTMLAttributes, ForwardedRef } from "react"
import { Property } from "csstype"
import { styled, StyleProps, CSS, CSSFn, callCSSFn } from "../styles"
import { boxCss } from "../Box"

export const flexCss = {
  ...boxCss,
  display: `flex`,
} as CSS
interface FlexProps {
  flexDirection?: Property.FlexDirection
  flexGrow?: Property.FlexGrow
  alignItems?: Property.AlignItems
  justifyContent?: Property.JustifyContent
  margin?: Property.Margin
  padding?: Property.Padding
  width?: Property.Width
  height?: Property.Height
  overflowY?: Property.OverflowY
  maxWidth?: Property.MaxWidth
  minWidth?: Property.MinWidth
  minHeight?: Property.MinHeight
  position?: Property.Position
  top?: Property.Top
  bottom?: Property.Bottom
  left?: Property.Left
  right?: Property.Right
  css?: CSSFn
  as?: `div` | `main` | `li` | `details`
}

type StyledFlexProps = FlexProps & StyleProps

type Component = HTMLElement | HTMLDivElement | HTMLLIElement

const makeCss = (props: StyledFlexProps) => ({
  ...flexCss,
  flexDirection: props.flexDirection,
  alignItems: props.alignItems,
  justifyContent: props.justifyContent,
  width: props.width,
  minWidth: props.minWidth,
  height: props.height,
  minHeight: props.minHeight,
  maxWidth: props.maxWidth,
  margin: props.margin,
  padding: props.padding,
  position: props.position,
  top: props.top,
  bottom: props.bottom,
  right: props.right,
  left: props.left,
  ...callCSSFn(props),
})

const StyledFlexDiv = styled.div<FlexProps & HTMLAttributes<HTMLDivElement>>((props: StyledFlexProps) => makeCss(props))
const StyledFlexMain = styled.main<FlexProps & HTMLAttributes<HTMLElement>>((props: StyledFlexProps) => makeCss(props))
const StyledFlexDetails = styled.details<FlexProps & HTMLAttributes<HTMLElement>>((props: StyledFlexProps) =>
  makeCss(props)
)
const StyledFlexListItem = styled.li<FlexProps & HTMLAttributes<HTMLLIElement>>((props: StyledFlexProps) =>
  makeCss(props)
)

export const Flex = forwardRef<Component, FlexProps & HTMLAttributes<Component>>(
  ({ as, ...props }: FlexProps, forwardedRef) => {
    switch (as) {
      case `main`:
        return <StyledFlexMain ref={forwardedRef as ForwardedRef<HTMLElement>} {...props} />
      case `details`:
        return <StyledFlexDetails ref={forwardedRef as ForwardedRef<HTMLDetailsElement>} {...props} />
      case `li`:
        return <StyledFlexListItem ref={forwardedRef as ForwardedRef<HTMLLIElement>} {...props} />
      case `div`:
      default:
        return <StyledFlexDiv ref={forwardedRef as ForwardedRef<HTMLDivElement>} {...props} />
    }
  }
)

type FullWidthProps = Omit<FlexProps, `width` | `height` | `justifyContent` | `alignItems`>

export const FullScreenFlex = forwardRef<HTMLDivElement, FullWidthProps & HTMLAttributes<HTMLDivElement>>(
  ({ ...props }: FullWidthProps, forwardedRef) => (
    <Flex ref={forwardedRef} width="100%" height="100vh" alignItems="center" justifyContent="center" {...props} />
  )
)

Flex.defaultProps = {
  position: undefined,
  top: undefined,
  bottom: undefined,
  right: undefined,
  left: undefined,
  flexDirection: undefined,
  flexGrow: undefined,
  alignItems: undefined,
  justifyContent: undefined,
  margin: undefined,
  padding: undefined,
  width: undefined,
  height: undefined,
  overflowY: undefined,
  maxWidth: undefined,
  minWidth: undefined,
  minHeight: undefined,
  css: undefined,
  as: `div`,
}
